import React, { useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useCreateCategory } from "../../hooks/api/useCategory";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { RootState } from "../../store/store";
import { DefaultCategoryFormValues } from "./constants/categoryConstants";
import { CategoryFormData } from "./types/categoryTypes";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddCategoryGuidanceBanner from "./AddCategoryGuidanceBanner";

const getSchema = (t: (key: string, fallback: string) => string) =>
  Yup.object().shape({
    name: Yup.string().required(
      t("category-name-required", "Category name is required")
    ),
    description: Yup.string().optional().nullable(),
    parentId: Yup.string().optional().nullable()
  }) as Yup.ObjectSchema<CategoryFormData>;

interface AddCategoryModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a category is created successfully; use to refetch the category list. */
  onSuccess?: () => void | Promise<void>;
  /** Pre-select parent when adding a nested category (e.g. from ViewSubcategoryDetail). */
  initialParentId?: string;
}

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
  open,
  onClose,
  onSuccess,
  initialParentId
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();
  const createCategory = useCreateCategory();
  const categoryList = useSelector(
    (state: RootState) => state.categories.categoryList
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<CategoryFormData>({
    resolver: yupResolver(getSchema(t)),
    mode: "onChange",
    defaultValues: DefaultCategoryFormValues
  });

  useEffect(() => {
    if (open) {
      reset({
        name: "",
        description: "",
        parentId: initialParentId ?? undefined
      });
    }
  }, [open, initialParentId, reset]);

  const onSubmit = async (data: CategoryFormData) => {
    try {
      const submitData = {
        ...data,
        parentId: data.parentId?.trim() || undefined
      };
      await createCategory.mutateAsync(submitData);
      reset(DefaultCategoryFormValues);
      onClose();
      await onSuccess?.();
    } catch (error) {
      console.error("Failed to create category:", error);
    }
  };

  const isLoading = isSubmitting || createCategory.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {t("add-new-category", "Add New Category")}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={isLoading}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form wraps both content and buttons */}
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden"
          }}
        >
          {/* Scrollable Content Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: matchMD ? 2 : 3,
              pt: 2,
              display: "flex",
              flexDirection: "column",
              gap: matchMD ? 1.5 : 2,
              "&::-webkit-scrollbar": {
                width: "8px"
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "10px"
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: "#555"
                }
              }
            }}
          >
            <AddCategoryGuidanceBanner />

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("category-name", "Category Name")}
                  placeholder={t(
                    "category-name-placeholder",
                    "Enter category name"
                  )}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isLoading}
                  autoFocus
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("description", "Description")}
                  placeholder={t(
                    "category-description-placeholder",
                    "Enter category description"
                  )}
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="parentId"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  error={!!errors.parentId}
                  disabled={isLoading}
                >
                  <InputLabel id="add-category-parent-label" shrink>
                    {t("parent-category", "Parent Category")}
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="add-category-parent-label"
                    label={t("parent-category", "Parent Category")}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(e.target.value || undefined)
                    }
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography component="span" color="text.secondary">
                            {t(
                              "select-parent-category-optional",
                              "Select parent category (optional)"
                            )}
                          </Typography>
                        );
                      }
                      return (
                        categoryList.find((c) => c.id === selected)?.name ??
                        selected
                      );
                    }}
                  >
                    <MenuItem value="">
                      <em>
                        {t(
                          "none-top-level-category",
                          "None (top-level category)"
                        )}
                      </em>
                    </MenuItem>
                    {categoryList.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.parentId && (
                    <FormHelperText>{errors.parentId.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Box>

          {/* Fixed Action Buttons - Inside Form */}
          <Box
            sx={{
              display: "flex",
              flexDirection: matchMD ? "column" : "row",
              gap: matchMD ? 1 : 2,
              p: matchMD ? 2 : 3,
              pt: matchMD ? 1.5 : 2,
              borderTop: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
              backgroundColor: "background.paper"
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isLoading}
              fullWidth
              size={matchMD ? "medium" : "large"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER }
              }}
            >
              {t("add-category", "Add Category")}
            </LoadingButton>

            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              disabled={isLoading}
              fullWidth
              size={matchMD ? "medium" : "large"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: "white",
                color: BRAND_COLOR,
                border: `1px solid ${BRAND_COLOR}`,
                "&:hover": {
                  backgroundColor: BRAND_HOVER,
                  color: "white",
                  borderColor: BRAND_HOVER
                }
              }}
            >
              {t("cancel", "Cancel")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddCategoryModal;
